<script setup lang="ts">
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import {pagesPath} from '@/ts/router/pages-path';
import {useIfuUpdateNotice} from '@/ts/composables/stateful/use-ifu-update-notice';

const emit = defineEmits<{
  acknowledged: [];
}>();

const showModal = defineModel<boolean>({required: true});
const {isAcknowledging, acknowledge} = useIfuUpdateNotice();

/**
 * Records the confirmation and closes the notice. Bound to the button only: the dialog asks the
 * user to confirm they have read and understood, and following the link says neither, so the link
 * opens the IFU in its own tab and leaves the notice standing.
 *
 * The notice closes whether or not the call reached the backend. A failed call leaves the user
 * unrecorded, so the notice returns on their next login — which is the right outcome, and better
 * than holding someone in a dialog they cannot clear because the network is down.
 */
async function handleAcknowledge(): Promise<void> {
  await acknowledge();
  showModal.value = false;
  emit('acknowledged');
}
</script>

<template>
  <balm-dialog
    v-model="showModal"
    theme="neutral-0"
    type="fit-content"
    :no-x="true"
    :no-backdrop-dismiss="true"
    persistent
    role="dialog"
    aria-labelledby="ifu-update-modal-title"
    aria-modal="true"
  >
    <div class="ifu-update-modal">
      <div class="modal-header">
        <h2 id="ifu-update-modal-title" class="modal-title">
          {{ $t('ifu_update_title') }}
        </h2>
      </div>

      <div class="modal-content">
        <p class="ifu-description">
          {{ $t('ifu_update_description') }}
        </p>

        <p class="ifu-where">
          <a
            :href="pagesPath.public.instructionsForUse"
            target="_blank"
            rel="noopener noreferrer"
            class="ifu-link"
          >{{ $t('ifu_update_read_link') }}</a>
        </p>

        <div class="buttons">
          <balm-btn
            :label="$t('confirm')"
            theme="blue"
            paddings="p2-5"
            :loading="isAcknowledging"
            :disable="isAcknowledging"
            @click="handleAcknowledge"
          />
        </div>
      </div>
    </div>
  </balm-dialog>
</template>

<style scoped lang="scss">
.ifu-update-modal {
  padding: var(--Spacing-spacing-4);
  max-height: 90vh;
  overflow-y: auto;

  @include screen-lg {
    min-width: 500px;
    max-width: 700px;
  }
}

.modal-header {
  text-align: center;
}

.modal-title {
  margin-bottom: var(--Spacing-spacing-4);
}

.ifu-description {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-100);
  line-height: 1.5;
  margin-bottom: var(--Spacing-spacing-3);
}

.ifu-where {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-100);
  line-height: 1.5;
  margin-bottom: var(--Spacing-spacing-4);
}

.ifu-link {
  color: var(--Primary-Blue-primary-blue-100);
  text-decoration: underline;
}

.buttons {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-3);
  justify-content: center;
  margin-top: var(--Spacing-spacing-6);
  margin-bottom: var(--Spacing-spacing-6);
}
</style>
